import { useEffect, useRef } from 'react';

// Cloudflare Turnstile widget. Renders nothing until Cloudflare's script
// has loaded, then reports each token through `onToken` — and `null`
// whenever the token stops being usable (expired, errored, or reset).
//
// Tokens are single-use: the API verifies (and so spends) one on every
// submit, accepted or not. Bump `resetKey` after each attempt so the next
// one gets a fresh token.

type TurnstileApi = {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () =>
        window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing'));
      script.onerror = () => {
        // Let a later mount try again rather than caching the failure.
        scriptPromise = null;
        script.remove();
        reject(new Error('turnstile script failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

export function Turnstile({
  siteKey,
  onToken,
  resetKey,
  theme = 'light',
  className,
}: {
  siteKey: string;
  onToken: (token: string | null) => void;
  /** Change it to discard the current token and run the check again. */
  resetKey?: unknown;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);
  // Kept in a ref so a parent re-rendering with a new closure doesn't
  // tear the widget down and re-run the challenge.
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    let cancelled = false;
    loadTurnstile()
      .then((api) => {
        if (cancelled || !containerRef.current) return;
        widgetRef.current = api.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          size: 'flexible',
          callback: (token: string) => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        });
      })
      .catch(() => onTokenRef.current(null));
    return () => {
      cancelled = true;
      if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [siteKey, theme]);

  const firstReset = useRef(true);
  useEffect(() => {
    if (firstReset.current) {
      firstReset.current = false;
      return;
    }
    onTokenRef.current(null);
    if (widgetRef.current && window.turnstile) window.turnstile.reset(widgetRef.current);
  }, [resetKey]);

  return <div ref={containerRef} className={className} />;
}
